import React, { RefObject, useEffect, useRef, useState } from 'react'
import { Box, Flex, Grid, styled } from '@chilipiper/design-system'
import { LinkButton, UnstyledButton } from '@chilipiper/design-system/src/new'
import * as R from 'remeda'
import { useLocale } from '@chilipiper/locale/src/new'
import { getSlotLabel } from '@chilipiper/booking-flow'
import { useTranslations } from '../../../context/TranslationContext'
import { customerColorCss } from './styles'

export interface TimesEvents {
  scrollIntoView(): void
}

interface Props {
  eventsRef?: RefObject<TimesEvents | null | undefined>
  onTimeChange: (time: string) => void
  startTimes?: Array<string>
  time?: string
  suggestedTimesToggle?: React.ReactNode
  shouldSubmitFormOnChange?: boolean
  timezone: string
}

export const Times = ({
  onTimeChange,
  startTimes,
  time,
  eventsRef,
  suggestedTimesToggle,
  shouldSubmitFormOnChange,
  timezone,
}: Props) => {
  const timeSlotsRef = useRef<HTMLDivElement>(null)
  const locale = useLocale()
  useEffect(() => {
    if (eventsRef) {
      eventsRef.current = {
        scrollIntoView: () => {
          setTimeout(
            () => timeSlotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
            0
          )
        },
      }
      return () => (eventsRef.current = null)
    }
    return R.doNothing()
  }, [eventsRef])

  const [showMore, setShowMore] = useState(false)
  const startTimesToShow = showMore ? startTimes : startTimes?.slice(0, 8)

  return (
    <Box px={5}>
      <Grid rowGap={3} columnGap={2} gridTemplateColumns='1fr 1fr' py={5} ref={timeSlotsRef}>
        {startTimesToShow?.map(startTime => {
          const { hour, period } = getSlotLabel(new Date(startTime), locale, timezone)

          return (
            <TimeButton
              key={startTime}
              bg='bg/button-moderate-default'
              color='fg/major'
              borderRadius={10}
              py='13px'
              textAlign='center'
              fontSize={14}
              fontWeight={600}
              onClick={() => onTimeChange(startTime)}
              isSelected={startTime === time}
              type={shouldSubmitFormOnChange ? 'submit' : 'button'}
            >
              {`${hour} ${period}`}
            </TimeButton>
          )
        })}
      </Grid>
      <ShowMoreOrSuggestedTimesToggle
        showMore={startTimes?.length !== startTimesToShow?.length}
        onShowMoreClick={() => setShowMore(!showMore)}
        suggestedTimesToggle={suggestedTimesToggle}
      />
    </Box>
  )
}

const ShowMoreOrSuggestedTimesToggle = ({
  showMore,
  onShowMoreClick,
  suggestedTimesToggle,
}: {
  showMore: boolean
  onShowMoreClick: () => void
  suggestedTimesToggle: React.ReactNode
}) => {
  const [showMoreText] = useTranslations(['showMore'])
  if (showMore || suggestedTimesToggle) {
    return (
      <ShowMoreContainer>
        <LinkButton variant='minor' onClick={onShowMoreClick}>
          {showMoreText ?? 'Show more'}
        </LinkButton>
      </ShowMoreContainer>
    )
  }
  if (suggestedTimesToggle) {
    return <ShowMoreContainer>{suggestedTimesToggle}</ShowMoreContainer>
  }
  return null
}

const ShowMoreContainer = styled(Flex)`
  justify-content: center;
  padding: ${({ theme }) => theme.space[4]} 0;
  box-shadow: ${({ theme }) => theme.shadows['border/row-top']};
`

const TimeButton = styled(UnstyledButton)<{ isSelected: boolean }>`
  transition:
    background-color 0.2s,
    color 0.2s;

  ${({ isSelected }) => (isSelected ? customerColorCss : undefined)}
  :hover, :focus {
    ${customerColorCss}
  }
`
